import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Check, X, Minus, Pencil, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTrainingQuizzes } from '../hooks/useTrainingQuizzes';
import type { TrainingQuiz } from '../types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface QuizEditorProps {
  moduleId: string;
}

interface SortableQuizItemProps {
  quiz: TrainingQuiz;
  index: number;
  isEditing: boolean;
  editingQuestion: {
    question: string;
    options: string[];
    correct_answer_index: number;
  };
  setEditingQuestion: React.Dispatch<React.SetStateAction<{
    question: string;
    options: string[];
    correct_answer_index: number;
  }>>;
  onStartEditing: (quiz: TrainingQuiz) => void;
  onCancelEditing: () => void;
  onSaveEditing: () => void;
  onDelete: (id: string) => void;
  canSubmitEdit: boolean;
  updateEditingOption: (index: number, value: string) => void;
  addEditingOption: () => void;
  removeEditingOption: (index: number) => void;
}

const SortableQuizItem: React.FC<SortableQuizItemProps> = ({
  quiz,
  index,
  isEditing,
  editingQuestion,
  setEditingQuestion,
  onStartEditing,
  onCancelEditing,
  onSaveEditing,
  onDelete,
  canSubmitEdit,
  updateEditingOption,
  addEditingOption,
  removeEditingOption,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: quiz.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  if (isEditing) {
    return (
      <motion.div
        ref={setNodeRef}
        style={style}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-4 space-y-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <span className="text-blue-400 text-sm font-medium">Editando pregunta</span>
        </div>
        
        <Input
          placeholder="Escribe la pregunta"
          value={editingQuestion.question}
          onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
          className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
        />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm">Opciones (mínimo 2, máximo 4):</p>
            {editingQuestion.options.length < 4 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={addEditingOption}
                className="text-blue-400 hover:bg-blue-500/20 h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Agregar opción
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {editingQuestion.options.map((option, optIndex) => (
              <div 
                key={optIndex} 
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all group ${
                  editingQuestion.correct_answer_index === optIndex
                    ? 'bg-emerald-500/20 border-emerald-500/40'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                onClick={() => setEditingQuestion({ ...editingQuestion, correct_answer_index: optIndex })}
              >
                <div 
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    editingQuestion.correct_answer_index === optIndex
                      ? 'border-emerald-400 bg-emerald-500'
                      : 'border-white/30'
                  }`}
                >
                  {editingQuestion.correct_answer_index === optIndex && (
                    <Check className="w-3.5 h-3.5 text-white" />
                  )}
                </div>
                <span className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center text-xs text-white/50 font-medium flex-shrink-0">
                  {String.fromCharCode(65 + optIndex)}
                </span>
                <Input
                  placeholder={`Opción ${String.fromCharCode(65 + optIndex)}`}
                  value={option}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateEditingOption(optIndex, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-0 text-white placeholder:text-white/40 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto"
                />
                {editingQuestion.options.length > 2 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEditingOption(optIndex);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0 transition-opacity flex-shrink-0"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <p className="text-emerald-400/70 text-xs flex items-center gap-1">
            <Check className="w-3 h-3" />
            Haz clic en la opción que sea la respuesta correcta
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancelEditing}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={onSaveEditing}
            disabled={!canSubmitEdit}
            className="bg-blue-500 hover:bg-blue-600"
          >
            <Check className="w-4 h-4 mr-1" />
            Guardar Cambios
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl p-4 border border-white/10"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
          >
            <GripVertical className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-bold">
              {index + 1}
            </span>
            <p className="text-white font-medium pt-0.5 leading-relaxed">
              {quiz.question}
            </p>
          </div>
          <div className="mt-3 ml-10 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {quiz.options.map((option, optIndex) => (
              <div
                key={optIndex}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  optIndex === quiz.correct_answer_index
                    ? 'bg-emerald-500/20 border border-emerald-500/30'
                    : 'bg-white/5 border border-white/10'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                  optIndex === quiz.correct_answer_index
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/10 text-white/50'
                }`}>
                  {optIndex === quiz.correct_answer_index ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    String.fromCharCode(65 + optIndex)
                  )}
                </span>
                <span className={`text-sm ${
                  optIndex === quiz.correct_answer_index
                    ? 'text-emerald-300'
                    : 'text-white/70'
                }`}>
                  {option}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onStartEditing(quiz)}
            className="text-blue-400/50 hover:text-blue-400 hover:bg-blue-500/10"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(quiz.id)}
            className="text-red-400/50 hover:text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export const QuizEditor: React.FC<QuizEditorProps> = ({ moduleId }) => {
  const { quizzes, isLoading, createQuiz, updateQuiz, deleteQuiz, reorderQuizzes } = useTrainingQuizzes(moduleId);
  const [isAdding, setIsAdding] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', ''],
    correct_answer_index: 0,
  });
  
  // Estado para edición
  const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState({
    question: '',
    options: ['', ''],
    correct_answer_index: 0,
  });

  // Sensors for drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = quizzes.findIndex((q) => q.id === active.id);
      const newIndex = quizzes.findIndex((q) => q.id === over.id);
      
      const reorderedQuizzes = arrayMove(quizzes, oldIndex, newIndex);
      
      // Update order_position for all affected quizzes
      await reorderQuizzes.mutateAsync(
        reorderedQuizzes.map((quiz, index) => ({
          id: quiz.id,
          order_position: index,
        }))
      );
    }
  };

  const handleAddQuestion = async () => {
    // Only validate non-empty options
    const validOptions = newQuestion.options.filter(o => o.trim());
    if (!newQuestion.question || validOptions.length < 2) return;

    await createQuiz.mutateAsync({
      module_id: moduleId,
      question: newQuestion.question,
      options: validOptions,
      correct_answer_index: Math.min(newQuestion.correct_answer_index, validOptions.length - 1),
      order_position: quizzes.length,
    });

    setNewQuestion({
      question: '',
      options: ['', ''],
      correct_answer_index: 0,
    });
    setIsAdding(false);
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: newOptions });
  };

  const addOption = () => {
    if (newQuestion.options.length < 4) {
      setNewQuestion({ ...newQuestion, options: [...newQuestion.options, ''] });
    }
  };

  const removeOption = (index: number) => {
    if (newQuestion.options.length > 2) {
      const newOptions = newQuestion.options.filter((_, i) => i !== index);
      let newCorrectIndex = newQuestion.correct_answer_index;
      if (index === newQuestion.correct_answer_index) {
        newCorrectIndex = 0;
      } else if (index < newQuestion.correct_answer_index) {
        newCorrectIndex = newQuestion.correct_answer_index - 1;
      }
      setNewQuestion({ ...newQuestion, options: newOptions, correct_answer_index: newCorrectIndex });
    }
  };

  // Funciones para edición
  const startEditing = (quiz: TrainingQuiz) => {
    setEditingQuizId(quiz.id);
    setEditingQuestion({
      question: quiz.question,
      options: [...quiz.options],
      correct_answer_index: quiz.correct_answer_index,
    });
    setIsAdding(false);
  };

  const cancelEditing = () => {
    setEditingQuizId(null);
    setEditingQuestion({ question: '', options: ['', ''], correct_answer_index: 0 });
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuizId) return;
    
    const validOptions = editingQuestion.options.filter(o => o.trim());
    if (!editingQuestion.question || validOptions.length < 2) return;

    await updateQuiz.mutateAsync({
      id: editingQuizId,
      question: editingQuestion.question,
      options: validOptions,
      correct_answer_index: Math.min(editingQuestion.correct_answer_index, validOptions.length - 1),
    });

    cancelEditing();
  };

  const updateEditingOption = (index: number, value: string) => {
    const newOptions = [...editingQuestion.options];
    newOptions[index] = value;
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };

  const addEditingOption = () => {
    if (editingQuestion.options.length < 4) {
      setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options, ''] });
    }
  };

  const removeEditingOption = (index: number) => {
    if (editingQuestion.options.length > 2) {
      const newOptions = editingQuestion.options.filter((_, i) => i !== index);
      let newCorrectIndex = editingQuestion.correct_answer_index;
      if (index === editingQuestion.correct_answer_index) {
        newCorrectIndex = 0;
      } else if (index < editingQuestion.correct_answer_index) {
        newCorrectIndex = editingQuestion.correct_answer_index - 1;
      }
      setEditingQuestion({ ...editingQuestion, options: newOptions, correct_answer_index: newCorrectIndex });
    }
  };

  const canSubmit = newQuestion.question.trim() && 
    newQuestion.options.filter(o => o.trim()).length >= 2 &&
    !createQuiz.isPending;

  const canSubmitEdit = editingQuestion.question.trim() && 
    editingQuestion.options.filter(o => o.trim()).length >= 2 &&
    !updateQuiz.isPending;

  if (isLoading) {
    return <div className="animate-pulse bg-white/5 h-20 rounded-lg" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h5 className="text-white/70 text-sm font-medium">
          Preguntas del Quiz ({quizzes.length})
        </h5>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsAdding(true)}
          className="text-emerald-400 hover:bg-emerald-500/20"
          disabled={isAdding}
        >
          <Plus className="w-4 h-4 mr-1" />
          Agregar Pregunta
        </Button>
      </div>

      {/* Existing Questions with Drag & Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={quizzes.map(q => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {quizzes.map((quiz, index) => (
              <SortableQuizItem
                key={quiz.id}
                quiz={quiz}
                index={index}
                isEditing={editingQuizId === quiz.id}
                editingQuestion={editingQuestion}
                setEditingQuestion={setEditingQuestion}
                onStartEditing={startEditing}
                onCancelEditing={cancelEditing}
                onSaveEditing={handleUpdateQuestion}
                onDelete={(id) => deleteQuiz.mutate(id)}
                canSubmitEdit={canSubmitEdit}
                updateEditingOption={updateEditingOption}
                addEditingOption={addEditingOption}
                removeEditingOption={removeEditingOption}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add Question Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-4 space-y-4"
          >
            <Input
              placeholder="Escribe la pregunta"
              value={newQuestion.question}
              onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-white/60 text-sm">Opciones (mínimo 2, máximo 4):</p>
                {newQuestion.options.length < 4 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={addOption}
                    className="text-emerald-400 hover:bg-emerald-500/20 h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Agregar opción
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {newQuestion.options.map((option, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all group ${
                      newQuestion.correct_answer_index === index
                        ? 'bg-emerald-500/20 border-emerald-500/40'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    onClick={() => setNewQuestion({ ...newQuestion, correct_answer_index: index })}
                  >
                    <div 
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        newQuestion.correct_answer_index === index
                          ? 'border-emerald-400 bg-emerald-500'
                          : 'border-white/30'
                      }`}
                    >
                      {newQuestion.correct_answer_index === index && (
                        <Check className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <span className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center text-xs text-white/50 font-medium flex-shrink-0">
                      {String.fromCharCode(65 + index)}
                    </span>
                    <Input
                      placeholder={`Opción ${String.fromCharCode(65 + index)}`}
                      value={option}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateOption(index, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-transparent border-0 text-white placeholder:text-white/40 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto"
                    />
                    {newQuestion.options.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOption(index);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0 transition-opacity flex-shrink-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-emerald-400/70 text-xs flex items-center gap-1">
                <Check className="w-3 h-3" />
                Haz clic en la opción que sea la respuesta correcta
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setNewQuestion({
                    question: '',
                    options: ['', ''],
                    correct_answer_index: 0,
                  });
                }}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleAddQuestion}
                disabled={!canSubmit}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Check className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
